import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Send,
  Paperclip,
  Loader2,
  Minus,
  MessageCircle,
  AlertCircle,
  CheckCircle,
  FileIcon,
} from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { AuthenticatedImage } from '@/components/common/AuthenticatedImage';
import { useChatStore } from '@/stores/chatStore';
import {
  startChat,
  sendMessage,
  sendTypingIndicator,
  uploadImage,
  closeChat,
  getAttachmentUrl,
  type ChatMessage,
} from '@/services/chatService';
import { ChatImageViewer } from './ChatImageViewer';

// ─── Constants ───────────────────────────────────────────────

const TYPING_DEBOUNCE_MS = 2000;
const TYPING_CLEAR_MS = 3000;
const INACTIVITY_THRESHOLD_MS = 3 * 60 * 1000; // 3 minutes

// ─── Message Bubble ──────────────────────────────────────────

const MessageBubble: React.FC<{ msg: ChatMessage }> = ({ msg }) => {
  const [viewerOpen, setViewerOpen] = useState(false);
  const isUser = msg.sender_type === 'user';
  const isSystem = msg.sender_type === 'system' || msg.message_type === 'system';

  const timeStr = (() => {
    try {
      return format(new Date(msg.created_at), 'h:mm a');
    } catch {
      return '';
    }
  })();

  // System messages — centered, no bubble
  if (isSystem) {
    return (
      <div className="my-[8px] text-center">
        <span className="text-[12px] italic text-muted-foreground">{msg.message}</span>
      </div>
    );
  }

  // Image messages
  if (msg.message_type === 'image' && msg.attachments?.[0]) {
    const attachmentId = msg.attachments[0].id;
    return (
      <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'} mb-[8px]`}>
        <div className="flex max-w-[280px] flex-col">
          <button
            type="button"
            onClick={() => setViewerOpen(true)}
            className={`rounded-2xl p-[4px] text-[14px] leading-[20px] ${
              isUser
                ? 'bg-primary text-primary-foreground rounded-br-[4px]'
                : 'bg-muted text-foreground rounded-bl-[4px]'
            }`}
          >
            <AuthenticatedImage
              src={getAttachmentUrl(attachmentId)}
              alt={msg.message || 'Image'}
              className="rounded-md max-w-full max-h-[200px] cursor-pointer"
            />
          </button>
          <span
            className={`mt-[2px] text-[10px] text-muted-foreground ${isUser ? 'text-right' : 'text-left'}`}
          >
            {timeStr}
          </span>
        </div>
        <ChatImageViewer
          isOpen={viewerOpen}
          onClose={() => setViewerOpen(false)}
          imageUrl={getAttachmentUrl(attachmentId)}
        />
      </div>
    );
  }

  // File messages
  if (msg.message_type === 'file') {
    return (
      <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'} mb-[8px]`}>
        <div className="flex max-w-[280px] flex-col">
          <div
            className={`flex items-center gap-[6px] rounded-2xl px-[12px] py-[8px] text-[14px] leading-[20px] ${
              isUser
                ? 'bg-primary text-primary-foreground rounded-br-[4px]'
                : 'bg-muted text-foreground rounded-bl-[4px]'
            }`}
          >
            <FileIcon className="h-[14px] w-[14px] shrink-0" />
            <span>{msg.message}</span>
          </div>
          <span
            className={`mt-[2px] text-[10px] text-muted-foreground ${isUser ? 'text-right' : 'text-left'}`}
          >
            {timeStr}
          </span>
        </div>
      </div>
    );
  }

  // Text messages
  return (
    <div className={`flex w-full ${isUser ? 'justify-end' : 'justify-start'} mb-[8px]`}>
      <div className="flex max-w-[280px] flex-col">
        <div
          className={`rounded-2xl px-[12px] py-[8px] text-[14px] leading-[20px] ${
            isUser
              ? 'bg-primary text-primary-foreground rounded-br-[4px]'
              : 'bg-muted text-foreground rounded-bl-[4px]'
          }`}
        >
          <p className="m-0 whitespace-pre-wrap break-words text-inherit">{msg.message}</p>
        </div>
        <span
          className={`mt-[2px] text-[10px] text-muted-foreground ${isUser ? 'text-right' : 'text-left'}`}
        >
          {timeStr}
        </span>
      </div>
    </div>
  );
};

// ─── Typing Indicator ────────────────────────────────────────

const TypingDots: React.FC = () => (
  <div className="mb-[8px] flex justify-start">
    <div className="flex items-center gap-[4px] rounded-2xl rounded-bl-[4px] bg-muted px-[14px] py-[8px]">
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          className="h-[6px] w-[6px] rounded-full bg-muted-foreground"
          style={{
            animation: 'chatBounce 1.2s ease-in-out infinite',
            animationDelay: `${i * 0.15}s`,
          }}
        />
      ))}
    </div>
  </div>
);

// ─── ChatWindow ──────────────────────────────────────────────

export const ChatWindow: React.FC = () => {
  const reduceMotion = useReducedMotion();
  const room = useChatStore((s) => s.room);
  const messages = useChatStore((s) => s.messages);
  const isAdminTyping = useChatStore((s) => s.isAdminTyping);
  const isLoading = useChatStore((s) => s.isLoading);
  const lastUserMessageAt = useChatStore((s) => s.lastUserMessageAt);
  const toggleWidget = useChatStore((s) => s.toggleWidget);
  const setRoom = useChatStore((s) => s.setRoom);
  const setMessages = useChatStore((s) => s.setMessages);
  const addMessage = useChatStore((s) => s.addMessage);
  const recordUserMessage = useChatStore((s) => s.recordUserMessage);
  const clearSession = useChatStore((s) => s.clearSession);
  const error = useChatStore((s) => s.error);

  const [inputValue, setInputValue] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  // isStarting drives the disabled state on send/upload during the brief
  // window between startChat() and the first sendMessage() so the user
  // can't fire a second request before the room id exists.
  const [isStarting, setIsStarting] = useState(false);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearTypingRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Auto-scroll on new messages
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length, isAdminTyping]);

  // Inactivity warning check
  useEffect(() => {
    if (!lastUserMessageAt || room?.status !== 'active') {
      setShowInactivityWarning(false);
      return;
    }
    const timer = setInterval(() => {
      const elapsed = Date.now() - lastUserMessageAt;
      setShowInactivityWarning(elapsed >= INACTIVITY_THRESHOLD_MS);
    }, 10_000);
    return () => clearInterval(timer);
  }, [lastUserMessageAt, room?.status]);

  // ─── Handlers ──────────────────────────────────────────────

  const handleSend = async () => {
    if (!inputValue.trim() || isSending) return;

    const text = inputValue.trim();
    setInputValue('');
    setIsSending(true);

    // Clear typing indicator
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    if (clearTypingRef.current) clearTimeout(clearTypingRef.current);

    try {
      // If no room yet, start chat first then send the message
      let activeRoom = room;
      if (!activeRoom) {
        setIsStarting(true);
        const result = await startChat();
        activeRoom = result.room;
        setRoom(result.room);
        if (result.room.messages && result.room.messages.length > 0) {
          // Backend returns messages in DESC order. The UI renders ASC.
          // Sort defensively in case ordering changes server-side.
          const sorted = [...result.room.messages].sort(
            (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
          setMessages(sorted);
        }
      }

      const msg = await sendMessage(activeRoom.id, text);
      addMessage(msg);
      recordUserMessage();
      setShowInactivityWarning(false);
    } catch (err: unknown) {
      const errMsg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Failed to send message';
      toast.error(errMsg);
      setInputValue(text); // Restore message on failure
    } finally {
      // Always reset both flags — previously setIsStarting was only reset
      // on the happy path inside try, so a failure during startChat would
      // leave the widget permanently in the "starting" state.
      setIsStarting(false);
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSend();
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);

    // Typing indicator (debounce)
    if (room?.status === 'active') {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      if (clearTypingRef.current) clearTimeout(clearTypingRef.current);

      typingTimeoutRef.current = setTimeout(() => {
        sendTypingIndicator(room.id, true).catch(() => {});
        clearTypingRef.current = setTimeout(() => {
          sendTypingIndicator(room.id, false).catch(() => {});
        }, TYPING_CLEAR_MS);
      }, TYPING_DEBOUNCE_MS);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !room) return;

    if (!['image/jpeg', 'image/jpg', 'image/png'].includes(file.type)) {
      toast.error('Only JPG and PNG images are allowed.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be smaller than 5MB.');
      return;
    }

    setIsUploading(true);
    try {
      const msg = await uploadImage(room.id, file);
      addMessage(msg);
      recordUserMessage();
    } catch {
      toast.error('Failed to upload image.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleCloseChat = useCallback(async () => {
    if (!room) return;
    try {
      await closeChat(room.id);
      setRoom({ ...room, status: 'closed' });
    } catch {
      toast.error('Failed to close chat.');
    }
  }, [room, setRoom]);

  // ─── Render ────────────────────────────────────────────────

  return (
    // Unified chat panel shell across PE / admin / webapp:
    // 380×520 white card, rounded-2xl, brand border, shadow-2xl, subtle
    // scale+fade entrance from bottom-right. Header is solid brand-primary
    // (no gradient/hardcoded hex). Reduced-motion users get an instant
    // open via motion/react.
    <motion.div
      initial={reduceMotion ? false : { scale: 0.95, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={reduceMotion ? { opacity: 0 } : { scale: 0.95, opacity: 0 }}
      style={{ transformOrigin: 'bottom right' }}
      transition={{ duration: reduceMotion ? 0 : 0.2 }}
      className="flex h-[520px] w-[380px] max-h-[80vh] flex-col overflow-hidden rounded-2xl border border-border bg-white shadow-2xl max-[420px]:w-[calc(100vw-2rem)]"
    >
      {/* ── Header — unified across PE / webapp: small avatar circle +
          title + dynamic subtitle. Close uses Minus (semantic "minimize"
          — session persists). The "End chat" action is the explicit
          "actually close conversation" button. ──────────────────────── */}
      <div className="flex flex-shrink-0 items-center justify-between rounded-t-2xl bg-primary px-[16px] py-[12px] text-primary-foreground">
        <div className="flex min-w-0 items-center gap-[12px]">
          <div className="flex h-[28px] w-[28px] shrink-0 items-center justify-center rounded-full bg-white/15">
            <MessageCircle className="h-[16px] w-[16px]" aria-hidden="true" />
          </div>
          <div className="min-w-0 leading-tight">
            <p className="truncate text-[14px]/[20px] font-semibold">Live Support</p>
            <p className="truncate text-[11px] opacity-80">
              {room?.status === 'pending'
                ? 'Connecting you to an agent…'
                : room?.status === 'active'
                  ? 'Agent connected'
                  : room?.status === 'closed'
                    ? 'Chat ended'
                    : 'We typically reply quickly'}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-[4px]">
          {room?.status === 'active' && (
            <button
              type="button"
              onClick={() => {
                void handleCloseChat();
              }}
              className="rounded bg-white/15 px-[8px] py-[4px] text-[12px] transition-colors hover:bg-white/25"
            >
              End Chat
            </button>
          )}
          <button
            type="button"
            onClick={toggleWidget}
            aria-label="Minimize chat"
            title="Minimize"
            className="rounded p-[4px] transition-colors hover:bg-white/15"
          >
            <Minus className="h-[16px] w-[16px]" />
          </button>
        </div>
      </div>

      {/* ── Body ──────────────────────────────────────────── */}
      <div className="flex flex-1 flex-col overflow-y-auto px-[16px] py-[16px] bg-white">
        {/* Loading state */}
        {isLoading && (
          <div className="flex h-full items-center justify-center">
            <div className="h-[24px] w-[24px] animate-spin rounded-full border-2 border-primary border-t-transparent" />
          </div>
        )}

        {/* Idle state — no room and not loading */}
        {!room && !isLoading && (
          <div className="flex h-full flex-col items-center justify-center gap-[16px] px-[16px] text-center">
            <div className="flex h-[64px] w-[64px] items-center justify-center rounded-full bg-primary/10">
              <MessageCircle className="h-[32px] w-[32px] text-primary" />
            </div>
            <div>
              <h3 className="text-[16px] font-semibold text-foreground">Need help?</h3>
              <p className="mt-[4px] text-[14px] text-muted-foreground">
                Type your message below to start a chat.
              </p>
            </div>
          </div>
        )}

        {/* Closed empty state */}
        {room?.status === 'closed' && messages.length === 0 && !isLoading && (
          <div className="flex h-full flex-col items-center justify-center gap-[16px] px-[16px] text-center">
            <div className="flex h-[64px] w-[64px] items-center justify-center rounded-full bg-emerald-50">
              <CheckCircle className="h-[32px] w-[32px] text-emerald-500" />
            </div>
            <div>
              <h3 className="text-[16px] font-semibold text-foreground">Chat ended</h3>
              <p className="mt-[4px] text-[14px] text-muted-foreground">
                Thank you for contacting support.
              </p>
            </div>
            <button
              type="button"
              onClick={() => {
                clearSession();
              }}
              className="text-[14px] font-medium text-primary hover:underline"
            >
              Start a new chat
            </button>
          </div>
        )}

        {/* Pending banner — shown above messages */}
        {room?.status === 'pending' && !isLoading && (
          <div className="mb-[12px] rounded-lg border border-primary/20 bg-primary/5 p-[10px] text-center">
            <div className="flex items-center justify-center gap-[8px]">
              <div className="h-[16px] w-[16px] animate-spin rounded-full border-2 border-primary border-t-transparent" />
              <span className="text-[14px] font-medium text-primary">Waiting for an agent…</span>
            </div>
            {room.queue_position !== null && room.queue_position !== undefined && (
              <p className="mt-[4px] text-[12px] text-muted-foreground">
                Queue position: {room.queue_position}
              </p>
            )}
          </div>
        )}

        {/* Messages */}
        {!isLoading && messages.map((msg) => <MessageBubble key={msg.id} msg={msg} />)}

        {/* Admin typing indicator */}
        {isAdminTyping && <TypingDots />}

        {/* Closed banner — when messages exist AND room closed */}
        {room?.status === 'closed' && messages.length > 0 && (
          <div className="mt-[16px] rounded-lg border border-emerald-200 bg-emerald-50 p-[12px] text-center">
            <div className="mb-[8px] flex items-center justify-center gap-[8px]">
              <CheckCircle className="h-[20px] w-[20px] text-emerald-500" />
              <span className="text-[14px] font-medium text-foreground">Chat ended</span>
            </div>
            <button
              type="button"
              onClick={() => {
                clearSession();
              }}
              className="text-[14px] font-medium text-primary hover:underline"
            >
              Start a new chat
            </button>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* ── Idle nudge ────────────────────────────────────────
          Soft amber banner shown after `INACTIVITY_THRESHOLD_MS` of no
          user messages on an active chat. Intentionally friendly + no
          countdown — the prior "may close soon" copy alarmed users
          without telling them when. The same banner copy is mirrored
          in PE's LiveChatWidget so both apps read identically. */}
      {showInactivityWarning && room?.status === 'active' && (
        <div className="flex flex-shrink-0 items-center gap-[6px] border-t border-amber-200 bg-amber-50 px-[12px] py-[8px]">
          <AlertCircle className="h-[14px] w-[14px] shrink-0 text-amber-600" />
          <span className="text-[11px] text-amber-800">
            No activity for a while — send a message to keep the chat going.
          </span>
        </div>
      )}

      {/* ── Input Area ────────────────────────────────────── */}
      {room?.status !== 'closed' && (
        <div className="border-t border-border bg-white p-[12px]">
          {error && (
            <div className="mb-[8px] rounded bg-destructive/10 px-[8px] py-[6px] text-[12px] text-destructive">
              {error}
            </div>
          )}
          <div className="flex items-end gap-[8px]">
            {/* Image upload */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading || isSending || isStarting || !room}
              aria-label="Attach image"
              title={!room ? 'Send a message first to start the chat' : undefined}
              className="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-md border border-border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isUploading ? (
                <Loader2 className="h-[16px] w-[16px] animate-spin" />
              ) : (
                <Paperclip className="h-[18px] w-[18px]" />
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                void handleFileChange(e);
              }}
            />
            {/* Textarea */}
            <textarea
              ref={textareaRef}
              value={inputValue}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={
                isStarting
                  ? 'Starting chat…'
                  : room?.status === 'pending'
                    ? 'Waiting for an agent…'
                    : 'Type a message...'
              }
              disabled={isSending || isStarting}
              rows={1}
              className="flex-1 resize-none rounded-md border border-border bg-white px-[10px] py-[8px] text-[14px] leading-[20px] text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
            />
            {/* Send */}
            <button
              type="button"
              onClick={() => {
                void handleSend();
              }}
              disabled={!inputValue.trim() || isSending || isStarting}
              aria-label="Send message"
              className="flex h-[36px] w-[36px] shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
            >
              {isSending || isStarting ? (
                <Loader2 className="h-[16px] w-[16px] animate-spin" />
              ) : (
                <Send className="h-[16px] w-[16px]" />
              )}
            </button>
          </div>
        </div>
      )}

      {/* ── CSS for typing dots animation ─────────────────── */}
      <style>{`
        @keyframes chatBounce {
          0%, 60%, 100% { transform: translateY(0); }
          30% { transform: translateY(-4px); }
        }
      `}</style>
    </motion.div>
  );
};
